import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { 
  Button, 
  TextField, 
  Box, 
  Stack, 
  Typography, 
  Divider,
  CircularProgress,
  Alert
} from "@mui/material";
import AddIcon from '@mui/icons-material/Add';
import { Edit } from "@mui/icons-material";
import { BaseHttpService } from "cms/core/services";
import { useQuery } from 'react-query';

function TranslationForm({ id, ...props }) {
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm();
  const [editingItem, setEditingItem] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState(false);
  const [values, setValues] = useState([]);

  const { data: languagesData, isLoading: languagesLoading } = useQuery(
    'languages', 
    BaseHttpService.loadLanguages,
    {
      cacheTime: 60*60*24*30*1000
    }
  );

  const languages = languagesData?.results || [];

  useEffect(() => {
    if (id) {
      BaseHttpService.loadTranslation(id).then(item => {
        setEditingItem(item);
        setValue("key", item.key);
        setValues(item.values || []);
      });
    }
  }, [id, setValue]);

  const handleValueChange = (languageId, newValue) => {
    const existingValue = values.find(v => v.language === languageId);
    
    if (existingValue) {
      setValues(values.map(v => 
        v.language === languageId ? { ...v, value: newValue } : v
      ));
    } else {
      setValues([...values, { value: newValue, language: languageId }]);
    }
  };

  const getValueForLanguage = (languageId) => {
    const valueObj = values.find(v => v.language === languageId);
    return valueObj ? valueObj.value : "";
  };

  const saveChanges = (data) => {
    setError(false);
    setDone(false);
    setLoading(true);

    const body = {
      "id": id || editingItem?.id,
      "key": data.key,
      "values": values,
    };

    BaseHttpService.saveTranslation(body)
      .then(response => {
        setLoading(false);
        setDone(true);
        setError(false);
        props.onEditDone(response);
      })
      .catch(err => {
        setLoading(false);
        setError("Something went wrong. Please try again.");
      });
  };

  return (
    <Box>
      <form onSubmit={handleSubmit(saveChanges)}>
        <Stack spacing={3}>
          {/* Key Field */}
          <TextField
            fullWidth
            label="Key"
            value={watch('key') ?? ""}
            variant="outlined"
            size="small"
            {...register("key", { required: "Key is required" })}
            error={!!errors.key}
            helperText={errors.key?.message}
            sx={{
              '& .MuiInputBase-input': {
                color: 'text.primary'
              },
              '& .MuiInputLabel-root': {
                color: 'text.secondary'
              }
            }}
          />

          <Divider />

          {/* Values for each language */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary', fontWeight: 600 }}>
              Translation Values
            </Typography>
            {languagesLoading ? (
              <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Stack spacing={2}>
                {languages.map((language) => (
                  <TextField
                    key={language.id}
                    fullWidth
                    label={`Value (${language.name})`}
                    value={getValueForLanguage(language.id)}
                    onChange={(e) => handleValueChange(language.id, e.target.value)}
                    variant="outlined"
                    size="small"
                    className="kfont"
                    sx={{
                      '& .MuiInputBase-input': {
                        color: 'text.primary'
                      },
                      '& .MuiInputLabel-root': {
                        color: 'text.secondary'
                      }
                    }}
                  />
                ))}
              </Stack>
            )}
          </Box>

          {/* Status Messages */}
          {done && (
            <Alert severity="success">
              Translation {editingItem ? 'updated' : 'created'} successfully!
            </Alert>
          )}
          {error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          {/* Action Buttons */}
          <Box display="flex" justifyContent="flex-end" gap={2} pt={1}>
            <Button
              variant="outlined"
              onClick={() => props.onEditDone?.()}
              disabled={loading}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} /> : (editingItem ? <Edit /> : <AddIcon />)}
              disabled={loading}
            >
              {loading ? 'Saving...' : (editingItem ? 'Save Changes' : 'Add Translation')}
            </Button>
          </Box>
        </Stack>
      </form>
    </Box>
  );
}

export default TranslationForm;
